import { FC, PropsWithChildren, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	CheckOutline,
	Divider,
	Flex,
	forwardRef,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Portal,
	RadioGroup,
	Stack,
	StackDivider,
	StyleProps,
	Text,
	Tooltip,
} from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { usePrivacy } from '../components/privacy/hooks/use-privacy';
import { PrivacyOption } from '../components/privacy/privacy-option';
import { useActionInput } from '../context';
import { useIsEnabled } from '../hooks';
import { usePrivacyTooltipText } from './privacy/hooks/use-privacy-tooltip-text';
import { PrivacyButton } from './privacy/privacy-button';

type ActionPrivacyProps = StyleProps;

export const ActionPrivacy = forwardRef<ActionPrivacyProps, 'button'>((props, ref) => {
	const { privacy, setPrivacy } = useActionInput();
	const { options } = usePrivacy();
	const shouldRender = useIsEnabled('privacy');
	const { t } = useTranslation('action_input');
	const { startDraft } = useActionInput();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

	const handleSelect = useCallback(
		(optionValue: typeof privacy, onClose: VoidFunction) => {
			setPrivacy(optionValue);
			onClose();
		},
		[setPrivacy]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent, index: number, optionValue: typeof privacy, onClose: VoidFunction) => {
			switch (e.key) {
				case 'Enter':
				case ' ':
					e.preventDefault();
					handleSelect(optionValue, onClose);
					break;
				case 'ArrowDown':
					e.preventDefault();
					if (index < options.length - 1) {
						optionRefs.current[index + 1]?.focus();
					}
					break;
				case 'ArrowUp':
					e.preventDefault();
					if (index > 0) {
						optionRefs.current[index - 1]?.focus();
					}
					break;
				case 'Escape':
					e.preventDefault();
					onClose();
					break;
			}
		},
		[handleSelect, options.length]
	);

	if (!shouldRender) {
		return null;
	}

	// Redesigned version
	if (isHomepageRedesignEnabled) {
		const selectedIndex = options.findIndex((opt) => opt.value === privacy);
		const initialFocusIndex = selectedIndex >= 0 ? selectedIndex : 0;

		return (
			<Popover isLazy onOpen={startDraft} placement="bottom-start" returnFocusOnClose={false}>
				{({ onClose }) => (
					<>
						<ButtonWrapper>
							<PrivacyButton ref={ref} {...props} />
						</ButtonWrapper>

						<PopoverContent
							borderColor="border.soft"
							boxShadow="xl"
							w="sm"
							p={1}
							onAnimationEnd={() => {
								optionRefs.current[initialFocusIndex]?.focus();
							}}
						>
							<Box as="ul" role="listbox" aria-activedescendant={`privacy-option-${privacy}`}>
								{options.map((option, index) => {
									const isSelected = privacy === option.value;
									return (
										<Box
											key={option.value}
											id={`privacy-option-${option.value}`}
											as="li"
											role="option"
											aria-selected={isSelected}
											tabIndex={-1}
											cursor="pointer"
											listStyleType="none"
											py={3}
											px={4}
											borderRadius="md"
											bg={isSelected ? 'primary.bgAlpha' : 'transparent'}
											_hover={{ bg: isSelected ? 'primary.bgAlpha' : 'bg.subtle' }}
											_focus={{ bg: isSelected ? 'primary.bgAlpha' : 'bg.subtle', outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: '-2px' }}
											ref={(el: HTMLLIElement | null) => {
												optionRefs.current[index] = el;
											}}
											onMouseDown={(e: React.MouseEvent) => {
												e.preventDefault();
												e.stopPropagation();
												handleSelect(option.value, onClose);
											}}
											onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index, option.value, onClose)}
										>
											<Flex justifyContent="space-between" alignItems="center">
												<Box>
													<Text fontSize="md" fontWeight="medium" color={isSelected ? 'primary.fg' : 'text.default'}>
														{option.label}
													</Text>
													<Text fontSize="sm" color={isSelected ? 'primary.fg' : 'text.soft'}>
														{option.description}
													</Text>
												</Box>
												{isSelected && <CheckOutline boxSize={5} color="primary.fg" flexShrink={0} />}
											</Flex>
										</Box>
									);
								})}
							</Box>
						</PopoverContent>
					</>
				)}
			</Popover>
		);
	}

	// Original version
	return (
		<Popover isLazy onOpen={startDraft} placement="bottom-end" returnFocusOnClose={false}>
			{({ onClose }) => (
				<>
					<ButtonWrapper>
						<PrivacyButton ref={ref} {...props} />
					</ButtonWrapper>

					<Portal>
						<PopoverContent borderColor="border.soft" boxShadow="xl" w="sm">
							<Box color="text.default" fontSize="lg" p={3} pl={4} pt={4}>
								{t('privacy.privacy_select_title')}
							</Box>
							<Divider borderColor="border.soft" />
							<RadioGroup defaultValue={privacy}>
								<Stack divider={<StackDivider borderColor="border.soft" />} spacing={0}>
									{options.map((option) => (
										<PrivacyOption
											key={option.value}
											option={option}
											onChange={() => {
												setPrivacy(option.value);
												onClose();
											}}
										/>
									))}
								</Stack>
							</RadioGroup>
						</PopoverContent>
					</Portal>
				</>
			)}
		</Popover>
	);
});

const ButtonWrapper: FC<PropsWithChildren> = ({ children }) => {
	const { buttonEnabled } = usePrivacy();
	const tooltipText = usePrivacyTooltipText();

	if (buttonEnabled) {
		return <PopoverTrigger>{children}</PopoverTrigger>;
	} else {
		return (
			<Tooltip as="span" display="block" label={tooltipText} placement="bottom">
				{children}
			</Tooltip>
		);
	}
};

ActionPrivacy.displayName = 'ActionPrivacy';
